import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createBrowserRouter, type DataRouter } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { Button } from '@/components/ui/Button';
import { Loading } from '@/components/ui/Loading';
import { Mascot } from '@/components/ui/Mascot';
import { useI18n } from '@/i18n/context';
import { I18nProvider } from '@/i18n/I18nProvider';
import { openDatabase, toStorageError, type StorageError } from '@/services/db/database';
import { recoverInterruptedSessions } from '@/services/progress';
import { AppStateProvider } from './AppState';
import { useAppState } from './useAppState';
import { ParentSessionProvider } from './ParentSession';
import { routes } from './routes';

function StorageErrorScreen({ error }: { error: StorageError }) {
  const { t } = useI18n();
  const body = {
    unavailable: t('errors.storageUnavailable'),
    quota: t('errors.storageQuota'),
    blocked: t('errors.storageBlocked'),
    unknown: t('errors.storageUnknown'),
  }[error.problem];
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-4 px-4 text-center">
      <Mascot mood="think" className="size-32" />
      <h1 className="text-2xl font-black">{t('errors.storageTitle')}</h1>
      <p className="text-ink-muted">{body}</p>
      <Button onClick={() => window.location.reload()}>{t('errors.reload')}</Button>
    </main>
  );
}

/** Opens local storage before anything else, and explains failures instead of crashing. */
function StorageGate({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<'opening' | 'ready' | StorageError>('opening');
  useEffect(() => {
    openDatabase()
      .then(() => recoverInterruptedSessions())
      .then(() => setStatus('ready'))
      .catch((error: unknown) => setStatus(toStorageError(error)));
  }, []);
  if (status === 'ready') return children;
  return (
    <I18nProvider locale="ar">
      {status === 'opening' ? <Loading label="…" /> : <StorageErrorScreen error={status} />}
    </I18nProvider>
  );
}

/** The interface language follows the active learner, then the device setting. */
function LocaleBridge({ children }: { children: ReactNode }) {
  const { activeLearner, settings } = useAppState();
  return (
    <I18nProvider locale={activeLearner?.preferences.locale ?? settings.locale}>
      {children}
    </I18nProvider>
  );
}

export function App({ router, banner }: { router?: DataRouter; banner?: ReactNode }) {
  const appRouter = useMemo(() => router ?? createBrowserRouter(routes), [router]);
  return (
    <StorageGate>
      <AppStateProvider>
        <LocaleBridge>
          <ParentSessionProvider>
            {banner}
            <RouterProvider router={appRouter} />
          </ParentSessionProvider>
        </LocaleBridge>
      </AppStateProvider>
    </StorageGate>
  );
}
